import * as React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { useRouter } from 'hooks/useRouter'
import { useDispatch, useSelector } from 'react-redux'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
// hooks
import { useRisksAPI } from 'hooks/risks/useRiskAPI'
import { useRiskPermissions } from 'hooks/risks'
// graphql
import {
  RISKS_QUERY,
  RISKS_PUBLIC_QUERY,
  LINKED_FORMS_PERMISSIONS_QUERY,
  GROUPED_FORMS_PERMISSIONS_QUERY
} from 'graphql/risk/queries'
// types
import { TRefetch } from 'types/refetch'
import { NetworkStatus } from '@apollo/client'
import {
  IRisk,
  IPermissionsMap,
  IRisksQueryArgs,
  IRiskUpdateInput,
  ISystemProcessRelatedForm,
  ILinkedFormsPermissionsMap,
  IGroupedFormsPermissionsMap,
  IGetLinkedFormsPermissionsArgs,
  IGetGroupedFormsPermissionsResponse
} from 'types/graphql'
// store
import { updateRelatedForm } from 'store/reducers/relatedForms'
import { offlineRiskSelector } from 'store/reducers/offlineRisk'
// utils
import { get } from 'lodash'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type Props = React.PropsWithChildren<unknown>

// The set an entry came from, attached by useRiskRelatedForms; absent when a caller reads the
// store directly.
export type RelatedFormWithOwner = ISystemProcessRelatedForm & { ownerId: string }

type GetGroupedFormsPermissions = {
  getGroupedFormsPermissions: IGetGroupedFormsPermissionsResponse
}

type GetLinkedFormsPermissions = {
  getLinkedFormsPermissions: ILinkedFormsPermissionsMap
}

interface IContext {
  risk: IRisk
  isLoading: boolean
  updateRisk: (input: IRiskUpdateInput) => Promise<void>
  riskRefetch: TRefetch<any, IRisksQueryArgs>
  showArchivedForms: boolean
  createRelatedRisk: (relatedForm: RelatedFormWithOwner, isScheduled?: boolean) => Promise<void>
  linkedFormsPermissions?: IPermissionsMap[]
  toggleShowArchivedForms: () => void
  groupedFormsPermissions?: IGroupedFormsPermissionsMap[]
  refetchLinkedRisksPermissions: TRefetch<GetLinkedFormsPermissions, IGetLinkedFormsPermissionsArgs>
}

export const RiskAPIContext = React.createContext<IContext>({} as any)

const RiskPageAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const [showArchivedForms, setShowArchivedForms] = React.useState(false)

  const isPublic = React.useContext(PublicContext)

  const offlineRisk = useSelector(offlineRiskSelector)

  const { createRisk, updateRisk: updateRiskMutation } = useRisksAPI()

  const riskId = router.query.id as string
  const isTempId = riskId?.startsWith('temp_')

  const {
    data: risksQuery,
    networkStatus,
    refetch
  } = useQuery(isPublic ? RISKS_PUBLIC_QUERY : RISKS_QUERY, {
    skip: !!router.query.multiple || !riskId || isTempId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        ids: [riskId],
        riskTemplateId: router.query.templateId
      },
      ...(isPublic && { domain: router.query.domain })
    }
  })

  // TODO: skip if no linkedForms
  const { data: formsPermissionsQuery } = useQuery<GetGroupedFormsPermissions>(GROUPED_FORMS_PERMISSIONS_QUERY, {
    skip: !riskId || isPublic || isTempId,
    variables: {
      riskId
    }
  })

  // TODO: skip if no linkedForms
  const { data: linkedFormsPermissionsQuery, refetch: refetchLinkedRisksPermissions } = useQuery<
    GetLinkedFormsPermissions,
    IGetLinkedFormsPermissionsArgs
  >(LINKED_FORMS_PERMISSIONS_QUERY, {
    skip: !riskId || isPublic || isTempId,
    variables: {
      riskId
    }
  })

  const groupedFormsPermissions = formsPermissionsQuery?.getGroupedFormsPermissions?.groupedFormsPermissionsMap
  const linkedFormsPermissions = linkedFormsPermissionsQuery?.getLinkedFormsPermissions?.permissionsMap

  const risk = get(risksQuery, isPublic ? 'risksPublic.data[0]' : 'risks.data[0]') ?? offlineRisk
  const isLoading = [NetworkStatus.loading, NetworkStatus.setVariables].includes(networkStatus)

  const permissions = useRiskPermissions(risk)

  const toggleShowArchivedForms = () => setShowArchivedForms((prev) => !prev)

  const createRelatedRisk: IContext['createRelatedRisk'] = async (relatedForm, isScheduled) => {
    try {
      // The owner of the set the tab came from: an entry from this record's own set hangs the new
      // record off this record, one from the parent's set off the parent. Every caller gets its
      // entries from useRiskRelatedForms, which attaches the owner.
      const relatedRisk = await createRisk({
        riskTemplateId: relatedForm.riskTemplateId,
        parentRiskId: relatedForm.ownerId,
        ...(isScheduled && { recurrence: { active: false } })
      })

      router.push(`/forms/edit/${relatedRisk.id}/template/${relatedForm.riskTemplateId}`)

      if (relatedRisk) {
        dispatch(
          updateRelatedForm({
            id: relatedForm.ownerId,
            form: { ...relatedForm, riskId: relatedRisk.id } as ISystemProcessRelatedForm
          })
        )
      }
    } catch (e) {
      console.log('[RiskPageAPI.createRelatedRisk]:', parseGraphQLErrorMessages(e))
    }
  }

  const updateRisk: IContext['updateRisk'] = async (input) => {
    try {
      await updateRiskMutation(risk?.id || riskId, input)

      await refetch()
    } catch (error) {
      console.log('[RiskPageAPI.updateRisk]: ', parseGraphQLErrorMessages(error))
    }
  }

  const value = React.useMemo(
    () => ({
      risk: {
        ...risk,
        permissions
      },
      isLoading,
      updateRisk,
      createRelatedRisk,
      showArchivedForms,
      riskRefetch: refetch,
      linkedFormsPermissions,
      toggleShowArchivedForms,
      groupedFormsPermissions,
      refetchLinkedRisksPermissions
    }),
    [risk, isLoading, permissions, showArchivedForms, linkedFormsPermissions]
  )

  return <RiskAPIContext.Provider value={value}>{children}</RiskAPIContext.Provider>
}

export default RiskPageAPI
